import type { ReactNode } from 'react';

import { cn } from '~/utils/cn';

import { type Crumb, PageBreadcrumbs } from './page-breadcrumbs';
import { PageHelpScope } from './page-help';

interface Props {
  breadcrumbs?: Crumb[];
  /** Optional thumbnail / icon left of the title (products). */
  media?: ReactNode;
  title: ReactNode;
  /** Small chips right after the title (kind, archived…). */
  chips?: ReactNode;
  /** The state chip — sits with the actions so state and "what to do" read together. */
  status?: ReactNode;
  /** Primary action first, then secondary, then ⋯. */
  actions?: ReactNode;
  /** 3–5 short facts, rendered as one muted line joined by · (replaces fact tiles). */
  meta?: ReactNode[];
  /** Renders `meta` as a wrapping row of self-contained chips (M-A) instead of a "·"-joined
   *  sentence — for a meta line whose facts are already pill-shaped (icon + text). */
  metaChips?: boolean;
  /** ⓘ help trigger (a `HelpPanel trigger="icon"`). */
  help?: ReactNode;
  /** Optional second line under the meta (e.g. the phase strip). */
  children?: ReactNode;
  className?: string | undefined;
}

/**
 * The one-band page header (R1): breadcrumb, then name + chips on the left and
 * status + one primary action on the right, then a single meta line of key facts.
 * Keeps the top of every page short so the content starts high.
 */
export function EntityHeader({
  breadcrumbs,
  media,
  title,
  chips,
  status,
  actions,
  meta,
  metaChips = false,
  help,
  children,
  className,
}: Props) {
  const facts = (meta ?? []).filter(
    (m) => m !== null && m !== undefined && m !== false && m !== '',
  );
  return (
    <header className={cn('flex flex-col gap-2', className)}>
      {breadcrumbs && breadcrumbs.length > 0 ? (
        <div className="flex items-center justify-between gap-2">
          <PageBreadcrumbs items={breadcrumbs} />
          {help ? <PageHelpScope>{help}</PageHelpScope> : null}
        </div>
      ) : null}
      <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-2">
        <div className="flex min-w-0 flex-1 items-start gap-3">
          {media ? <div className="shrink-0">{media}</div> : null}
          <div className="flex min-w-0 flex-col gap-1">
            <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
              <h1 className="text-foreground min-w-0 text-xl leading-tight font-semibold">
                {title}
              </h1>
              {chips}
              {!breadcrumbs?.length && help ? <PageHelpScope>{help}</PageHelpScope> : null}
            </div>
            {facts.length > 0 ? (
              metaChips ? (
                <div className="flex flex-wrap items-center gap-1.5">
                  {facts.map((f, i) => (
                    <span key={i} className="inline-flex min-w-0 items-center">
                      {f}
                    </span>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground flex flex-wrap items-center gap-x-2 gap-y-0.5 text-sm">
                  {facts.map((f, i) => (
                    <span key={i} className="inline-flex min-w-0 items-center gap-2">
                      {i > 0 ? (
                        <span aria-hidden="true" className="text-border">
                          ·
                        </span>
                      ) : null}
                      <span className="min-w-0">{f}</span>
                    </span>
                  ))}
                </p>
              )
            ) : null}
          </div>
        </div>
        {status || actions ? (
          <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
            {status}
            {actions}
          </div>
        ) : null}
      </div>
      {children}
    </header>
  );
}
